import { parseQueryString, appendQueryParameters } from '@flowio/web-sdk';
import initFraud from './initFraud';

const optin = require('@flowio/optin');
const persistSession = require('@flowio/session/lib/persist-session');
const SessionClient = require('@flowio/session/lib/client');
const request = require('@flowio/request');
const session = require('@flowio/session');
const storage = require('@flowio/storage');
const get = require('lodash/get');
const config = require('./config');
const cart = require('./cart');
const clearExperience = require('./utilities/clear-experience');
const optionsError = require('./utilities/options-error');
const optionsSuccess = require('./utilities/options-success');
const syncCartAttributes = require('./utilities/sync-cart-attributes');
const { getCart } = require('./cart_unified/cart-unified');

const EXPERIENCE_PARAM_KEY = 'flow_experience';
const COUNTRY_PARAM_KEY = 'flow_country';
const ENABLED_EXPERIENCE_PARAM_KEY = 'flow_enabled';

const sessionClient = new SessionClient({ apiBaseUri: config.API_BASE_URI });

/**
 * Return the current experience from the session.
 *
 * @return {string} The experience key, e.g: canada
 */
function getExperience() {
  return session.getExperience();
}

/**
 * Return the current country from the session.
 *
 * @return {string} An ISO 3166-3 country code
 */
function getCountry() {
  return session.getCountry();
}

function getCountriesFromStorage() {
  return storage.session.get('countries') || storage.memory.get('countries');
}

/**
 * Store countries into Storage. Falls back to memory storage if browser
 * storage is unavailable.
 * @param {array} countries List of country object from the API
 */
function setCountriesInStorage(countries) {
  try {
    storage.session.set('countries', countries);
  } catch (error) {
    // Storage may not be available
    storage.memory.set('countries', countries);
  }
}

/**
 * Get a list of valid countries based on the shop's Flow organization.
 *
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, countries)
 * @param {function} options.error      Optional. error(status, error)
 */
function getCountries(options = {}) {
  const countries = getCountriesFromStorage();
  const country = session.getCountry();
  const ipAddress = session.getIpAddress();
  const url = `${config.API_BASE_URI}/${config.FLOW_ORGANIZATION}/countries`;
  const params = {};

  if (countries) {
    if (options.success) {
      options.success(countries);
    }
    return;
  }

  if (ipAddress) {
    params.ip = ipAddress;
  }

  if (country) {
    params.country = country;
  }

  const urlWithParams = appendQueryParameters(url, params);

  request.authenticated({
    url: urlWithParams,
  }, (status, response) => {
    if (response.error) {
      if (options.error) {
        options.error(new Error(`Could not get countries: ${response.error.messages.join(', ')}`));
      }
      return;
    }

    setCountriesInStorage(response);

    if (options.success) {
      options.success(response);
    }
  });
}

/**
 * Update the session with the given country. This will yield an updated
 * session with a new experience selected based on the country provided.
 *
 * @param {string} country              A valid ISO 3166-3 country code
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, session)
 * @param {function} options.error      Optional. error(status, error)
 */
function setCountry(country, options = {}) {
  const sessionId = session.getSessionId();

  if (!country) {
    if (options.error) {
      options.error(new Error('A valid ISO 3166-3 country code is required. See https://api.flow.io/reference/countries'));
    }
    return;
  }

  sessionClient.resetSession({
    integration: 'shopify',
    sessionId,
    geo: {
      country,
    },
    onSuccess: (updatedSession) => {
      persistSession(updatedSession);
      initFraud();
      syncCartAttributes(updatedSession, getCart()).then(() => {
        if (updatedSession.experience) {
          const geo = {
            ...session.getGeo(),
            experience: get(updatedSession, 'experience.key'),
          };

          cart.setGeo(geo, {
            // Return the session not the updated cart
            success: () => {
              optionsSuccess(options, updatedSession);
            },
            error: error => optionsError(options, error),
          });
        // If no updatedSession.experience.key, user has gone back to domestic mode.
        // The cart is technically out of sync with the session, but it won't
        // be in use either. Ideally there would be a way to let the cart know
        // this.
        } else {
          optionsSuccess(options, updatedSession);
        }

        optin.getAndDisplayOptin({ targetElement: get(window, 'Flow.optinTarget') });
      });
    },
    onError: (error) => {
      optionsError(options, new Error(`Could not update session with new Experience: ${error.message}`));
    },
  });
}

/**
 * Update the session with the given experience. This will yield an updated
 * session with a new experience selected based on the country provided.
 *
 * @param {string} experience An experience key that is valid for the
 *                            organization assigned to the session
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, session)
 * @param {function} options.error      Optional. error(status, error)
 */
function setExperience(experience, options = {}) {
  const sessionId = session.getSessionId();

  sessionClient.resetSession({
    integration: 'shopify',
    sessionId,
    experience,
    onSuccess: (updatedSession) => {
      persistSession(updatedSession);
      initFraud();
      syncCartAttributes(updatedSession, getCart()).then(() => {
        optionsSuccess(options, updatedSession);
        optin.getAndDisplayOptin({ targetElement: get(window, 'Flow.optinTarget') });
      });
    },
    onError: error => optionsError(options, new Error(`Could not update session with new Experience: ${error.message}`)),
  });
}

function resetSessionByUrlParams(options = {}) {
  const params = parseQueryString(window.location.search);
  const experienceParam = params[EXPERIENCE_PARAM_KEY];
  const countryParam = params[COUNTRY_PARAM_KEY];
  const enabledParam = params[ENABLED_EXPERIENCE_PARAM_KEY];

  if (experienceParam) {
    setExperience(experienceParam, options);
  } else if (countryParam) {
    setCountry(countryParam, options);
  } else if (enabledParam === 'false') {
    clearExperience(options);
  } else if (options.success) {
    options.success();
  }
}

module.exports = {
  getExperience,
  setExperience,
  getCountries,
  getCountry,
  setCountry,

  // Internal
  resetSessionByUrlParams,
};



// WEBPACK FOOTER //
// ./static/js/v0/experience.js